import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc, arrayRemove, query, orderBy, limit, where, Timestamp } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';

interface DashboardStats {
  totalUsers: number;
  activeUsers: number;
  bannedUsers: number;
  newUsersToday: number;
  totalCommunities: number;
  totalPosts: number;
  postsToday: number;
}

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<any[]>([]);
  const [communities, setCommunities] = useState<any[]>([]);
  const [posts, setPosts] = useState<any[]>([]);
  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selectedTab, setSelectedTab] = useState<'dashboard'|'users'|'communities'|'posts'|'banned'|'reports'|'settings'>('dashboard');
  const [bannedUsers, setBannedUsers] = useState<any[]>([]);
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    activeUsers: 0,
    bannedUsers: 0,
    newUsersToday: 0,
    totalCommunities: 0,
    totalPosts: 0,
    postsToday: 0
  });
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [bulkAction, setBulkAction] = useState<'ban'|'unban'|'delete'|'promote'|''>('');

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        // Récupérer les utilisateurs
        const usersSnap = await getDocs(collection(db, 'users'));
        const usersData = usersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setUsers(usersData);

        // Récupérer les communautés
        const commSnap = await getDocs(collection(db, 'communities'));
        const communitiesData = commSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setCommunities(communitiesData);

        // Récupérer les posts
        const postsSnap = await getDocs(collection(db, 'community_posts'));
        const postsData = postsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setPosts(postsData);

        // Calculer les utilisateurs bannis
        const bannedUsersData = usersData.filter((u: any) => u.banned === true);
        setBannedUsers(bannedUsersData);

        // Récupérer les signalements (si ils existent)
        try {
          const reportsSnap = await getDocs(collection(db, 'reports'));
          setReports(reportsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        } catch (err) {
          console.log('Collection reports non trouvée');
        }

        // Calculer les statistiques
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const newUsersToday = usersData.filter((u: any) => {
          if (!u.createdAt) return false;
          const userDate = u.createdAt.toDate ? u.createdAt.toDate() : new Date(u.createdAt);
          return userDate >= today;
        }).length;

        const postsToday = postsData.filter((p: any) => {
          if (!p.createdAt) return false;
          const postDate = p.createdAt.toDate ? p.createdAt.toDate() : new Date(p.createdAt);
          return postDate >= today;
        }).length;

        setStats({
          totalUsers: usersData.length,
          activeUsers: usersData.filter((u: any) => !u.banned).length,
          bannedUsers: bannedUsersData.length,
          newUsersToday,
          totalCommunities: communitiesData.length,
          totalPosts: postsData.length,
          postsToday
        });

      } catch (err: any) {
        setError('Erreur chargement dashboard: ' + (err.message || ''));
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  // Actions admin améliorées avec confirmation
  const handleBanUser = async (userId: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir bannir cet utilisateur ?')) return;
    
    try {
      await updateDoc(doc(db, 'users', userId), { 
        banned: true,
        bannedAt: Timestamp.now(),
        bannedBy: user.id
      });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, banned: true, bannedAt: new Date(), bannedBy: user.id } : u));
      setBannedUsers(prev => [...prev, users.find(u => u.id === userId)]);
    } catch (err: any) {
      setError('Erreur bannissement: ' + err.message);
    }
  };

  const handleUnbanUser = async (userId: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir débannir cet utilisateur ?')) return;
    
    try {
      await updateDoc(doc(db, 'users', userId), { 
        banned: false,
        unbannedAt: Timestamp.now(),
        unbannedBy: user.id
      });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, banned: false } : u));
      setBannedUsers(prev => prev.filter(u => u.id !== userId));
    } catch (err: any) {
      setError('Erreur débannissement: ' + err.message);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('ATTENTION: Cette action est irréversible. Supprimer définitivement cet utilisateur ?')) return;
    
    try {
      await deleteDoc(doc(db, 'users', userId));
      setUsers(prev => prev.filter(u => u.id !== userId));
      setBannedUsers(prev => prev.filter(u => u.id !== userId));
    } catch (err: any) {
      setError('Erreur suppression: ' + err.message);
    }
  };

  const handleChangeRole = async (userId: string, newRole: string) => {
    if (!window.confirm(`Changer le rôle de cet utilisateur vers ${newRole} ?`)) return;
    
    try {
      await updateDoc(doc(db, 'users', userId), { 
        role: newRole,
        roleChangedAt: Timestamp.now(),
        roleChangedBy: user.id
      });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
    } catch (err: any) {
      setError('Erreur changement de rôle: ' + err.message);
    }
  };

  const handleDeleteCommunity = async (commId: string) => {
    if (!window.confirm('ATTENTION: Supprimer cette communauté supprimera aussi tous ses posts. Continuer ?')) return;
    
    try {
      await deleteDoc(doc(db, 'communities', commId));
      setCommunities(prev => prev.filter(c => c.id !== commId));
      // Supprimer aussi les posts de la communauté
      const communityPosts = posts.filter(p => p.communityId === commId);
      for (const post of communityPosts) {
        await deleteDoc(doc(db, 'community_posts', post.id));
      }
      setPosts(prev => prev.filter(p => p.communityId !== commId));
    } catch (err: any) {
      setError('Erreur suppression communauté: ' + err.message);
    }
  };

  const handleDeletePost = async (postId: string) => {
    if (!window.confirm('Supprimer ce post ?')) return;
    
    try {
      await deleteDoc(doc(db, 'community_posts', postId));
      setPosts(prev => prev.filter(p => p.id !== postId));
    } catch (err: any) {
      setError('Erreur suppression post: ' + err.message);
    }
  };

  // Actions groupées
  const handleBulkAction = async () => {
    if (selectedUsers.length === 0 || !bulkAction) return;
    
    if (!window.confirm(`Appliquer l'action "${bulkAction}" à ${selectedUsers.length} utilisateur(s) ?`)) return;

    try {
      for (const userId of selectedUsers) {
        switch (bulkAction) {
          case 'ban':
            await handleBanUser(userId);
            break;
          case 'unban':
            await handleUnbanUser(userId);
            break;
          case 'delete':
            await handleDeleteUser(userId);
            break;
          case 'promote':
            await handleChangeRole(userId, 'moderator');
            break;
        }
      }
      setSelectedUsers([]);
      setBulkAction('');
    } catch (err: any) {
      setError('Erreur action groupée: ' + err.message);
    }
  };

  // Filtrage amélioré
  const filteredUsers = users.filter(u => {
    const searchTerm = search.toLowerCase();
    return (u.displayName || u.username || u.id).toLowerCase().includes(searchTerm) ||
           (u.walletAddress || '').toLowerCase().includes(searchTerm) ||
           (u.email || '').toLowerCase().includes(searchTerm);
  });

  const filteredCommunities = communities.filter(c => {
    const searchTerm = search.toLowerCase();
    return (c.name || c.id).toLowerCase().includes(searchTerm) ||
           (c.description || '').toLowerCase().includes(searchTerm);
  });

  const filteredPosts = posts.filter(p => {
    const searchTerm = search.toLowerCase();
    return (p.content || '').toLowerCase().includes(searchTerm) ||
           (p.authorId || '').toLowerCase().includes(searchTerm);
  });

  // Sécurité : accès admin/modérateur uniquement
  if (!user || !(user.role === 'admin' || user.role === 'moderator')) {
    return (
      <div className="min-h-screen cosmic-bg flex items-center justify-center">
        <div className="cosmic-card p-8 text-center max-w-md">
          <div className="text-red-400 text-6xl mb-4">🚫</div>
          <h2 className="text-xl font-bold text-red-400 mb-2">Accès Refusé</h2>
          <p className="text-gray-300">Cette section est réservée aux administrateurs et modérateurs.</p>
          <button 
            onClick={() => window.history.back()} 
            className="cosmic-button mt-4"
          >
            Retour
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen cosmic-bg flex items-center justify-center">
        <div className="cosmic-card p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-400 mx-auto mb-4"></div>
          <p className="text-emerald-300">Chargement du dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen cosmic-bg">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="cosmic-card p-6 mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500 flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Dashboard Admin</h1>
                <p className="text-gray-400">
                  Connecté en tant que {user.role === 'admin' ? 'Administrateur' : 'Modérateur'} - {user.displayName || user.username}
                </p>
              </div>
            </div>
            
            {/* Actions rapides */}
            <div className="flex flex-wrap gap-2">
              <button 
                onClick={() => window.location.reload()} 
                className="cosmic-button-secondary text-sm"
              >
                🔄 Actualiser
              </button>
              <button 
                onClick={() => setSelectedTab('settings')} 
                className="cosmic-button-secondary text-sm"
              >
                ⚙️ Paramètres
              </button>
            </div>
          </div>
        </div>

        {/* Messages d'erreur */}
        {error && (
          <div className="cosmic-card p-4 mb-6 border-l-4 border-red-500 bg-red-900/20">
            <div className="flex items-center">
              <div className="text-red-400 mr-3">⚠️</div>
              <p className="text-red-300">{error}</p>
              <button 
                onClick={() => setError('')} 
                className="ml-auto text-red-400 hover:text-red-300"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="cosmic-card p-1 mb-8">
          <nav className="flex flex-wrap gap-1">
            {[
              { id: 'dashboard', label: '📊 Dashboard', color: 'emerald' },
              { id: 'users', label: '👥 Utilisateurs', color: 'blue' },
              { id: 'communities', label: '🏘️ Communautés', color: 'cyan' },
              { id: 'posts', label: '📝 Posts', color: 'green' },
              { id: 'banned', label: '🚫 Bannis', color: 'red' },
              { id: 'reports', label: '🚨 Signalements', color: 'orange' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setSelectedTab(tab.id as any)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  selectedTab === tab.id
                    ? `bg-${tab.color}-500/30 text-${tab.color}-200 border border-${tab.color}-500/50`
                    : 'text-gray-400 hover:text-white hover:bg-gray-700/30'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Barre de recherche */}
        {selectedTab !== 'dashboard' && selectedTab !== 'settings' && (
          <div className="cosmic-card p-4 mb-6">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                className="cosmic-input pl-10 w-full"
                placeholder={`Rechercher dans ${selectedTab}...`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        )}

        {/* Contenu principal */}
        {selectedTab === 'dashboard' && (
          <div className="space-y-8">
            {/* Statistiques principales */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="cosmic-card p-6 text-center">
                <div className="text-3xl mb-2">👥</div>
                <div className="text-2xl font-bold text-emerald-400">{stats.totalUsers}</div>
                <div className="text-gray-400">Utilisateurs totaux</div>
                <div className="text-sm text-emerald-300 mt-1">+{stats.newUsersToday} aujourd'hui</div>
              </div>
              
              <div className="cosmic-card p-6 text-center">
                <div className="text-3xl mb-2">✅</div>
                <div className="text-2xl font-bold text-green-400">{stats.activeUsers}</div>
                <div className="text-gray-400">Utilisateurs actifs</div>
                <div className="text-sm text-gray-500">{((stats.activeUsers / stats.totalUsers) * 100).toFixed(1)}% du total</div>
              </div>
              
              <div className="cosmic-card p-6 text-center">
                <div className="text-3xl mb-2">🏘️</div>
                <div className="text-2xl font-bold text-cyan-400">{stats.totalCommunities}</div>
                <div className="text-gray-400">Communautés</div>
                <div className="text-sm text-cyan-300">{Math.floor(stats.totalUsers / stats.totalCommunities || 1)} membres/comm. moy.</div>
              </div>
              
              <div className="cosmic-card p-6 text-center">
                <div className="text-3xl mb-2">📝</div>
                <div className="text-2xl font-bold text-blue-400">{stats.totalPosts}</div>
                <div className="text-gray-400">Posts totaux</div>
                <div className="text-sm text-blue-300">+{stats.postsToday} aujourd'hui</div>
              </div>
            </div>

            {/* Alertes et notifications */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="cosmic-card p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                  🚨 Alertes récentes
                </h3>
                <div className="space-y-3">
                  {stats.bannedUsers > 0 && (
                    <div className="p-3 bg-red-900/20 border border-red-700/30 rounded-lg">
                      <div className="text-red-300 font-medium">{stats.bannedUsers} utilisateur(s) banni(s)</div>
                      <div className="text-sm text-gray-400">Nécessite une surveillance</div>
                    </div>
                  )}
                  {reports.length > 0 && (
                    <div className="p-3 bg-orange-900/20 border border-orange-700/30 rounded-lg">
                      <div className="text-orange-300 font-medium">{reports.length} signalement(s) en attente</div>
                      <div className="text-sm text-gray-400">À traiter rapidement</div>
                    </div>
                  )}
                  {stats.newUsersToday > 10 && (
                    <div className="p-3 bg-emerald-900/20 border border-emerald-700/30 rounded-lg">
                      <div className="text-emerald-300 font-medium">Forte croissance: +{stats.newUsersToday} nouveaux utilisateurs</div>
                      <div className="text-sm text-gray-400">Surveiller l'activité</div>
                    </div>
                  )}
                  {reports.length === 0 && stats.bannedUsers === 0 && (
                    <div className="p-3 bg-emerald-900/20 border border-emerald-700/30 rounded-lg text-center">
                      <div className="text-emerald-300">✅ Aucune alerte active</div>
                      <div className="text-sm text-gray-400">Tout fonctionne normalement</div>
                    </div>
                  )}
                </div>
              </div>

              <div className="cosmic-card p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                  📈 Activité récente
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-gray-700/30 rounded-lg">
                    <span className="text-gray-300">Posts aujourd'hui</span>
                    <span className="text-emerald-400 font-semibold">{stats.postsToday}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-700/30 rounded-lg">
                    <span className="text-gray-300">Nouveaux utilisateurs</span>
                    <span className="text-blue-400 font-semibold">{stats.newUsersToday}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-700/30 rounded-lg">
                    <span className="text-gray-300">Communautés actives</span>
                    <span className="text-cyan-400 font-semibold">{communities.filter(c => c.lastActivity && new Date(c.lastActivity) > new Date(Date.now() - 24*60*60*1000)).length}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Utilisateurs */}
        {selectedTab==='users' && (
          <div className="bg-emerald-900/20 rounded-xl p-4 mb-6 border border-emerald-800/30 shadow-xl">
            <h2 className="text-lg font-bold text-emerald-300 mb-2">Utilisateurs ({filteredUsers.length})</h2>
            <div className="flex flex-wrap gap-4">
              {filteredUsers.map(u => (
                <div key={u.id} className="flex flex-col items-center w-40 bg-emerald-900/10 rounded-lg p-2">
                  <span className="text-xs text-emerald-200 mt-1 truncate max-w-[120px] drop-shadow">{u.displayName || u.username || u.id}</span>
                  <span className={`text-[10px] mt-1 px-2 py-0.5 rounded ${u.role==='admin'?'bg-emerald-500/30 text-emerald-200':u.role==='moderator'?'bg-emerald-400/20 text-emerald-300':'bg-emerald-900/20 text-emerald-100'}`}>{u.role||'membre'}</span>
                  <span className={`text-[10px] mt-1 px-2 py-0.5 rounded ${u.banned?'bg-red-700/30 text-red-300':'bg-emerald-900/20 text-emerald-100'}`}>{u.banned?'Banni':'Actif'}</span>
                  <div className="mt-1 flex flex-col gap-1">
                    {u.role!=='admin' && <button className="text-[10px] px-2 py-0.5 rounded bg-cyan-400/30 text-cyan-200 hover:bg-cyan-400/50 transition" onClick={()=>handleChangeRole(u.id,'moderator')}>Promouvoir modérateur</button>}
                    {u.role==='moderator' && <button className="text-[10px] px-2 py-0.5 rounded bg-emerald-400/30 text-emerald-200 hover:bg-emerald-400/50 transition" onClick={()=>handleChangeRole(u.id,'member')}>Dégrader membre</button>}
                    <button className="text-[10px] px-2 py-0.5 rounded bg-red-400/30 text-red-200 hover:bg-red-400/50 transition" onClick={()=>handleBanUser(u.id)} disabled={u.banned}>Bannir</button>
                    <button className="text-[10px] px-2 py-0.5 rounded bg-emerald-400/30 text-emerald-200 hover:bg-emerald-400/50 transition" onClick={()=>handleUnbanUser(u.id)} disabled={!u.banned}>Débannir</button>
                    <button className="text-[10px] px-2 py-0.5 rounded bg-red-700/30 text-red-300 hover:bg-red-700/50 transition" onClick={()=>handleDeleteUser(u.id)}>Supprimer</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Communautés */}
        {selectedTab==='communities' && (
          <div className="bg-cyan-900/20 rounded-xl p-4 mb-6 border border-cyan-800/30 shadow-xl">
            <h2 className="text-lg font-bold text-cyan-300 mb-2">Communautés ({filteredCommunities.length})</h2>
            <div className="flex flex-wrap gap-4">
              {filteredCommunities.map(c => (
                <div key={c.id} className="flex flex-col items-center w-40 bg-cyan-900/10 rounded-lg p-2">
                  <span className="text-xs text-cyan-200 mt-1 truncate max-w-[120px] drop-shadow">{c.name || c.id}</span>
                  <span className="text-[10px] mt-1 px-2 py-0.5 rounded bg-cyan-400/20 text-cyan-300">{c.members?.length||0} membres</span>
                  <div className="mt-1 flex flex-col gap-1">
                    <button className="text-[10px] px-2 py-0.5 rounded bg-red-400/30 text-red-200 hover:bg-red-400/50 transition" onClick={()=>handleDeleteCommunity(c.id)}>Supprimer</button>
                    <a href={`/communities/${c.id}`} className="text-[10px] px-2 py-0.5 rounded bg-emerald-400/30 text-emerald-200 hover:bg-emerald-400/50 transition text-center">Accéder</a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Posts */}
        {selectedTab==='posts' && (
          <div className="bg-emerald-900/20 rounded-xl p-4 mb-6 border border-emerald-800/30 shadow-xl">
            <h2 className="text-lg font-bold text-emerald-300 mb-2">Posts ({filteredPosts.length})</h2>
            <div className="flex flex-wrap gap-4">
              {filteredPosts.map(p => (
                <div key={p.id} className="flex flex-col items-center w-40 bg-emerald-900/10 rounded-lg p-2">
                  <span className="text-xs text-emerald-200 mt-1 truncate max-w-[120px] drop-shadow">{p.content?.slice(0,40)||p.id}</span>
                  <span className="text-[10px] mt-1 px-2 py-0.5 rounded bg-emerald-400/20 text-emerald-300">Auteur: {p.authorId}</span>
                  <div className="mt-1 flex flex-col gap-1">
                    <button className="text-[10px] px-2 py-0.5 rounded bg-red-400/30 text-red-200 hover:bg-red-400/50 transition" onClick={()=>handleDeletePost(p.id)}>Supprimer</button>
                    {/* Bannir auteur */}
                    <button className="text-[10px] px-2 py-0.5 rounded bg-red-700/30 text-red-300 hover:bg-red-700/50 transition" onClick={()=>handleBanUser(p.authorId)}>Bannir auteur</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Bannis */}
        {selectedTab==='banned' && (
          <div className="bg-red-900/20 rounded-xl p-4 mb-6 border border-red-800/30 shadow-xl">
            <h2 className="text-lg font-bold text-red-300 mb-2">Utilisateurs bannis ({bannedUsers.length})</h2>
            <div className="flex flex-wrap gap-4">
              {bannedUsers.map(u => (
                <div key={u.id} className="flex flex-col items-center w-40 bg-red-900/10 rounded-lg p-2">
                  <span className="text-xs text-red-200 mt-1 truncate max-w-[120px] drop-shadow">{u.displayName || u.username || u.id}</span>
                  <span className="text-[10px] mt-1 px-2 py-0.5 rounded bg-red-700/30 text-red-300">Banni</span>
                  <div className="mt-1 flex flex-col gap-1">
                    <button className="text-[10px] px-2 py-0.5 rounded bg-emerald-400/30 text-emerald-200 hover:bg-emerald-400/50 transition" onClick={()=>handleUnbanUser(u.id)}>Débannir</button>
                    <button className="text-[10px] px-2 py-0.5 rounded bg-red-700/30 text-red-300 hover:bg-red-700/50 transition" onClick={()=>handleDeleteUser(u.id)}>Supprimer</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Statistiques */}
        <div className="bg-emerald-900/20 rounded-xl p-4 mb-6 border border-emerald-800/30 shadow-xl flex flex-wrap gap-8 justify-center">
          <div className="flex flex-col items-center">
            <span className="text-emerald-300 text-lg font-bold">Utilisateurs</span>
            <span className="text-emerald-200 text-2xl">{users.length}</span>
          </div>
          <div className="flex flex-col items-center">
            <span className="text-cyan-300 text-lg font-bold">Communautés</span>
            <span className="text-cyan-200 text-2xl">{communities.length}</span>
          </div>
          <div className="flex flex-col items-center">
            <span className="text-emerald-300 text-lg font-bold">Posts</span>
            <span className="text-emerald-200 text-2xl">{posts.length}</span>
          </div>
          <div className="flex flex-col items-center">
            <span className="text-red-300 text-lg font-bold">Bannis</span>
            <span className="text-red-200 text-2xl">{bannedUsers.length}</span>
          </div>
        </div>

        {/* Section Utilisateurs */}
        {selectedTab === 'users' && (
          <div className="space-y-6">
            {/* Actions groupées */}
            {selectedUsers.length > 0 && (
              <div className="cosmic-card p-4 border-l-4 border-blue-500">
                <div className="flex flex-wrap items-center gap-4">
                  <span className="text-blue-300 font-medium">
                    {selectedUsers.length} utilisateur(s) sélectionné(s)
                  </span>
                  <select
                    value={bulkAction}
                    onChange={(e) => setBulkAction(e.target.value as any)}
                    className="cosmic-input-small"
                  >
                    <option value="">Choisir une action</option>
                    <option value="ban">Bannir</option>
                    <option value="unban">Débannir</option>
                    <option value="promote">Promouvoir modérateur</option>
                    <option value="delete">Supprimer</option>
                  </select>
                  <button
                    onClick={handleBulkAction}
                    disabled={!bulkAction}
                    className="cosmic-button-secondary"
                  >
                    Appliquer
                  </button>
                  <button
                    onClick={() => setSelectedUsers([])}
                    className="cosmic-button-secondary"
                  >
                    Désélectionner
                  </button>
                </div>
              </div>
            )}

            <div className="cosmic-card">
              <div className="p-6 border-b border-gray-700">
                <h2 className="text-xl font-semibold text-white flex items-center">
                  👥 Gestion des utilisateurs ({filteredUsers.length})
                </h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-700/30">
                    <tr>
                      <th className="p-3 text-left">
                        <input
                          type="checkbox"
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedUsers(filteredUsers.map(u => u.id));
                            } else {
                              setSelectedUsers([]);
                            }
                          }}
                          className="rounded"
                        />
                      </th>
                      <th className="p-3 text-left text-gray-300">Utilisateur</th>
                      <th className="p-3 text-left text-gray-300">Rôle</th>
                      <th className="p-3 text-left text-gray-300">Statut</th>
                      <th className="p-3 text-left text-gray-300">Créé le</th>
                      <th className="p-3 text-left text-gray-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u: any) => (
                      <tr key={u.id} className="border-b border-gray-700/30 hover:bg-gray-700/20">
                        <td className="p-3">
                          <input
                            type="checkbox"
                            checked={selectedUsers.includes(u.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedUsers(prev => [...prev, u.id]);
                              } else {
                                setSelectedUsers(prev => prev.filter(id => id !== u.id));
                              }
                            }}
                            className="rounded"
                          />
                        </td>
                        <td className="p-3">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500 flex items-center justify-center text-white font-bold">
                              {(u.displayName || u.username || 'U').charAt(0)}
                            </div>
                            <div>
                              <div className="text-white font-medium">{u.displayName || u.username || 'Sans nom'}</div>
                              <div className="text-gray-400 text-sm">{u.walletAddress?.slice(0, 8)}...{u.walletAddress?.slice(-4)}</div>
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            u.role === 'admin' ? 'bg-emerald-500/20 text-emerald-300' :
                            u.role === 'moderator' ? 'bg-blue-500/20 text-blue-300' :
                            'bg-gray-500/20 text-gray-300'
                          }`}>
                            {u.role === 'admin' ? 'Admin' : u.role === 'moderator' ? 'Mod' : 'Membre'}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            u.banned ? 'bg-red-500/20 text-red-300' : 'bg-green-500/20 text-green-300'
                          }`}>
                            {u.banned ? 'Banni' : 'Actif'}
                          </span>
                        </td>
                        <td className="p-3 text-gray-400 text-sm">
                          {u.createdAt ? new Date(u.createdAt.seconds ? u.createdAt.seconds * 1000 : u.createdAt).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="p-3">
                          <div className="flex flex-wrap gap-1">
                            {!u.banned && (
                              <button
                                onClick={() => handleBanUser(u.id)}
                                className="text-xs px-2 py-1 rounded bg-red-500/20 text-red-300 hover:bg-red-500/30 transition"
                              >
                                Bannir
                              </button>
                            )}
                            {u.banned && (
                              <button
                                onClick={() => handleUnbanUser(u.id)}
                                className="text-xs px-2 py-1 rounded bg-green-500/20 text-green-300 hover:bg-green-500/30 transition"
                              >
                                Débannir
                              </button>
                            )}
                            {u.role !== 'admin' && user.role === 'admin' && (
                              <button
                                onClick={() => handleChangeRole(u.id, u.role === 'moderator' ? 'member' : 'moderator')}
                                className="text-xs px-2 py-1 rounded bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 transition"
                              >
                                {u.role === 'moderator' ? 'Dégrader' : 'Promouvoir'}
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteUser(u.id)}
                              className="text-xs px-2 py-1 rounded bg-red-700/20 text-red-300 hover:bg-red-700/30 transition"
                            >
                              Supprimer
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Section Communautés */}
        {selectedTab === 'communities' && (
          <div className="cosmic-card">
            <div className="p-6 border-b border-gray-700">
              <h2 className="text-xl font-semibold text-white flex items-center">
                🏘️ Gestion des communautés ({filteredCommunities.length})
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
              {filteredCommunities.map((c: any) => (
                <div key={c.id} className="p-4 bg-gray-700/30 rounded-lg border border-gray-600/30">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-white font-medium">{c.name || 'Sans nom'}</h3>
                      <p className="text-gray-400 text-sm">{c.description?.slice(0, 60)}...</p>
                    </div>
                    <span className="px-2 py-1 rounded bg-cyan-500/20 text-cyan-300 text-xs">
                      {c.members?.length || 0} membres
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <a
                      href={`/communities/${c.id}`}
                      className="text-xs px-3 py-1 rounded bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 transition"
                    >
                      Voir
                    </a>
                    <button
                      onClick={() => handleDeleteCommunity(c.id)}
                      className="text-xs px-3 py-1 rounded bg-red-500/20 text-red-300 hover:bg-red-500/30 transition"
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Section Posts */}
        {selectedTab === 'posts' && (
          <div className="cosmic-card">
            <div className="p-6 border-b border-gray-700">
              <h2 className="text-xl font-semibold text-white flex items-center">
                📝 Gestion des posts ({filteredPosts.length})
              </h2>
            </div>
            <div className="space-y-4 p-6">
              {filteredPosts.map((p: any) => (
                <div key={p.id} className="p-4 bg-gray-700/30 rounded-lg border border-gray-600/30">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <p className="text-white mb-2">{p.content?.slice(0, 200)}{p.content?.length > 200 ? '...' : ''}</p>
                      <div className="flex items-center space-x-4 text-sm text-gray-400">
                        <span>👤 {p.authorId}</span>
                        <span>🏘️ {p.communityId}</span>
                        <span>📅 {p.createdAt ? new Date(p.createdAt.seconds ? p.createdAt.seconds * 1000 : p.createdAt).toLocaleDateString() : 'N/A'}</span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 ml-4">
                      <button
                        onClick={() => handleDeletePost(p.id)}
                        className="text-xs px-3 py-1 rounded bg-red-500/20 text-red-300 hover:bg-red-500/30 transition"
                      >
                        Supprimer
                      </button>
                      <button
                        onClick={() => handleBanUser(p.authorId)}
                        className="text-xs px-3 py-1 rounded bg-red-700/20 text-red-300 hover:bg-red-700/30 transition"
                      >
                        Bannir auteur
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Section Utilisateurs bannis */}
        {selectedTab === 'banned' && (
          <div className="cosmic-card">
            <div className="p-6 border-b border-gray-700">
              <h2 className="text-xl font-semibold text-white flex items-center">
                🚫 Utilisateurs bannis ({bannedUsers.length})
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
              {bannedUsers.map((u: any) => (
                <div key={u.id} className="p-4 bg-red-900/20 rounded-lg border border-red-700/30">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center text-red-300 font-bold">
                      {(u.displayName || u.username || 'U').charAt(0)}
                    </div>
                    <div>
                      <div className="text-white font-medium">{u.displayName || u.username || 'Sans nom'}</div>
                      <div className="text-red-400 text-sm">Banni</div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-gray-400 text-sm">
                      Banni le: {u.bannedAt ? new Date(u.bannedAt.seconds ? u.bannedAt.seconds * 1000 : u.bannedAt).toLocaleDateString() : 'N/A'}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleUnbanUser(u.id)}
                        className="text-xs px-3 py-1 rounded bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 transition"
                      >
                        Débannir
                      </button>
                      <button
                        onClick={() => handleDeleteUser(u.id)}
                        className="text-xs px-3 py-1 rounded bg-red-700/20 text-red-300 hover:bg-red-700/30 transition"
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Section Signalements */}
        {selectedTab === 'reports' && (
          <div className="cosmic-card">
            <div className="p-6 border-b border-gray-700">
              <h2 className="text-xl font-semibold text-white flex items-center">
                🚨 Signalements ({reports.length})
              </h2>
            </div>
            <div className="p-6">
              {reports.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">✅</div>
                  <p className="text-gray-400">Aucun signalement en attente</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {reports.map((r: any) => (
                    <div key={r.id} className="p-4 bg-orange-900/20 rounded-lg border border-orange-700/30">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="text-white font-medium">{r.type || 'Signalement'}</h4>
                          <p className="text-gray-300 mt-1">{r.reason || 'Aucune raison spécifiée'}</p>
                          <div className="text-sm text-gray-400 mt-2">
                            Signalé par: {r.reporterId} | Cible: {r.targetId}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button className="text-xs px-3 py-1 rounded bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 transition">
                            Traiter
                          </button>
                          <button className="text-xs px-3 py-1 rounded bg-red-500/20 text-red-300 hover:bg-red-500/30 transition">
                            Ignorer
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Section Paramètres */}
        {selectedTab === 'settings' && (
          <div className="cosmic-card">
            <div className="p-6 border-b border-gray-700">
              <h2 className="text-xl font-semibold text-white flex items-center">
                ⚙️ Paramètres d'administration
              </h2>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-white font-medium mb-3">Configuration générale</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg">
                      <span className="text-gray-300">Mode maintenance</span>
                      <input type="checkbox" className="rounded" />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg">
                      <span className="text-gray-300">Inscription ouverte</span>
                      <input type="checkbox" defaultChecked className="rounded" />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg">
                      <span className="text-gray-300">Modération automatique</span>
                      <input type="checkbox" defaultChecked className="rounded" />
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-white font-medium mb-3">Limites et quotas</h3>
                  <div className="space-y-3">
                    <div className="p-3 bg-gray-700/30 rounded-lg">
                      <label className="text-gray-300 text-sm">Posts par jour (par utilisateur)</label>
                      <input type="number" defaultValue="50" className="cosmic-input mt-1" />
                    </div>
                    <div className="p-3 bg-gray-700/30 rounded-lg">
                      <label className="text-gray-300 text-sm">Taille max des images (MB)</label>
                      <input type="number" defaultValue="5" className="cosmic-input mt-1" />
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-700">
                <button className="cosmic-button-secondary">
                  Annuler
                </button>
                <button className="cosmic-button">
                  Sauvegarder
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default AdminDashboard;
